/* Same-day pickup uses Copenhagen time, independently of the visitor's time zone. */
(() => {
  const clock = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Copenhagen', year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  });

  function validDate(value) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
    const date = new Date(`${value}T00:00:00Z`);
    return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === value;
  }

  function minutes(value) {
    if (!/^(?:[01]\d|2[0-3]):[0-5]\d$/.test(value || '')) return null;
    const [hour, minute] = value.split(':').map(Number);
    return hour * 60 + minute;
  }

  function parseSchedule(data) {
    const weekdays = [minutes(data.weekdayOpen), minutes(data.weekdayClose)];
    const saturday = [minutes(data.saturdayOpen), minutes(data.saturdayClose)];
    if ([...weekdays, ...saturday].includes(null)) return null;
    if (weekdays[0] >= weekdays[1] || saturday[0] >= saturday[1]) return null;
    if (!/^\d+$/.test(String(data.preparationMinutes))) return null;
    const preparation = Number(data.preparationMinutes);
    if (preparation > 180) return null;
    const closures = [];
    for (const entry of (data.closedDates || '').split(/\r?\n/).map(line => line.trim()).filter(Boolean)) {
      const dates = entry.split('..').map(date => date.trim());
      if (dates.length > 2 || !dates.every(validDate)) return null;
      const [start, end = start] = dates;
      if (start > end) return null;
      closures.push([start, end]);
    }
    return { weekdays, saturday, preparation, closures };
  }

  function localTime(value) {
    const match = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})$/.exec(value || '');
    if (!match || !validDate(match[1]) || minutes(match[2]) === null) return null;
    return { date: match[1], weekday: new Date(`${match[1]}T00:00:00Z`).getUTCDay(), minute: minutes(match[2]) };
  }

  function copenhagenTime(now) {
    const parts = Object.fromEntries(clock.formatToParts(now).map(part => [part.type, part.value]));
    return localTime(`${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`);
  }

  function pickupState(schedule, time, inStock) {
    if (!schedule) return 'invalid_schedule';
    if (!time) return 'invalid_time';
    if (!inStock) return 'out_of_stock';
    if (schedule.closures.some(([start, end]) => time.date >= start && time.date <= end)) return 'closed_date';
    if (time.weekday === 0) return 'closed_day';
    const [open, close] = time.weekday === 6 ? schedule.saturday : schedule.weekdays;
    if (time.minute < open || time.minute >= close) return 'outside_hours';
    if (time.minute >= close - schedule.preparation) return 'preparation_cutoff';
    return 'available';
  }

  // The pure rules can also be checked with Node's built-in test runner.
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { parseSchedule, localTime, copenhagenTime, pickupState };
  }
  if (typeof customElements === 'undefined' || customElements.get('cw-pickup')) return;

  customElements.define('cw-pickup', class extends HTMLElement {
    static get observedAttributes() { return ['data-in-stock']; }

    connectedCallback() {
      this.schedule = parseSchedule(this.dataset);
      this.refresh = this.refresh || (() => this.updateVisibility());
      this.previewStatus = this.nextElementSibling?.matches('[data-pickup-preview-status]')
        ? this.nextElementSibling : null;
      document.addEventListener('visibilitychange', this.refresh);
      window.addEventListener('pageshow', this.refresh);
      window.addEventListener('focus', this.refresh);
      this.updateVisibility();
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      document.removeEventListener('visibilitychange', this.refresh);
      window.removeEventListener('pageshow', this.refresh);
      window.removeEventListener('focus', this.refresh);
    }

    attributeChangedCallback() {
      if (this.isConnected && this.refresh) this.updateVisibility();
    }

    updateVisibility() {
      clearTimeout(this.timer);
      let state = 'invalid_time';
      const previewAt = this.dataset.previewAt?.trim();
      try {
        // Liquid only supplies the simulation attribute inside Shopify's theme editor.
        const time = previewAt ? localTime(previewAt) : copenhagenTime(new Date());
        state = pickupState(this.schedule, time, this.dataset.inStock === 'true');
      } catch (_) {
        // Never show a same-day promise if the time cannot be established.
      }
      this.hidden = state !== 'available';
      this.previewStatus ||= this.nextElementSibling?.matches('[data-pickup-preview-status]')
        ? this.nextElementSibling : null;
      if (this.previewStatus) {
        const explanations = {
          available: 'Vises: lagerført og inden for bestillingsfristen.',
          out_of_stock: 'Skjult: den valgte variant har ikke bekræftet lagerbeholdning.',
          closed_date: 'Skjult: datoen er markeret som lukkedag.',
          closed_day: 'Skjult: søndag.',
          outside_hours: 'Skjult: uden for åbningstiden.',
          preparation_cutoff: 'Skjult: fristen for afhentning i dag er passeret.',
          invalid_schedule: 'Skjult: kontrollér åbningstider, klargøringstid og lukkedatoer.',
          invalid_time: 'Skjult: kontrollér testdatoen (ÅÅÅÅ-MM-DDTtt:mm).',
        };
        this.previewStatus.textContent = `Afhentningstest · ${previewAt || 'aktuel dansk tid'} · ${explanations[state]}`;
      }
      // Re-evaluate at the next minute boundary and immediately on returning to the tab.
      this.timer = setTimeout(this.refresh, 60000 - (Date.now() % 60000));
    }
  });
})();
